import { useLocalStorage, usePreferredDark } from '@vueuse/core';
import { defineStore } from 'pinia';

export type ThemeMode = 'light' | 'dark' | 'system';

export const THEME_COOKIE = 'tradel-theme';

export const useThemeStore = defineStore('theme', () => {
  const stored = useLocalStorage<ThemeMode>('tradel:theme', 'dark');
  const cookie = useCookie(THEME_COOKIE);
  const secHint = useRequestHeader('sec-ch-prefers-color-scheme');
  const systemPrefersDark = usePreferredDark();

  const resolved = computed<'light' | 'dark'>(() => {
    if (import.meta.server) {
      if (cookie.value === 'light' || cookie.value === 'dark') return cookie.value;
      const h = secHint?.toLowerCase();
      if (h === 'light') return 'light';
      return 'dark';
    }
    if (stored.value === 'system') return systemPrefersDark.value ? 'dark' : 'light';
    return stored.value;
  });

  const mode = computed<ThemeMode>(() => stored.value);
  const isDark = computed(() => resolved.value === 'dark');

  const setMode = (next: ThemeMode) => {
    stored.value = next;
  };

  const toggle = () => {
    stored.value = resolved.value === 'dark' ? 'light' : 'dark';
  };

  /** Keeps <html> + cookie in sync with local preference (matches public/theme-boot.js). */
  if (import.meta.client) {
    watchEffect(() => {
      const dark = resolved.value === 'dark';
      document.documentElement.classList.toggle('tradel-dark', dark);
      document.documentElement.setAttribute('data-tradel-theme', dark ? 'dark' : 'light');
      document.documentElement.style.colorScheme = dark ? 'dark' : 'light';
      document.documentElement.style.backgroundColor = dark ? '#191919' : '#ffffff';
      document.cookie = `${THEME_COOKIE}=${resolved.value};path=/;max-age=31536000;SameSite=Lax`;
    });
  }

  return { mode, resolved, isDark, setMode, toggle };
});
